import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentOrg, setCurrentOrg] = useState(null);

  const getOrgHeaders = useCallback(() => {
    if (currentOrg) return { 'X-Organization-Id': currentOrg.org_id };
    return {};
  }, [currentOrg]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      // Auto-select org from memberships
      if (data.memberships?.length > 0 && !currentOrg) {
        // Check cookie for saved org
        const savedOrgId = document.cookie.split('; ').find(c => c.startsWith('current_org_id='))?.split('=')[1];
        const savedOrg = savedOrgId ? data.memberships.find(m => m.org_id === savedOrgId) : null;
        setCurrentOrg(savedOrg || data.memberships[0]);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  // Install the silent-refresh interceptor BEFORE the initial /auth/me call so
  // that even the first request on a fresh page load (with an expired access
  // token) is intercepted and transparently refreshed instead of logging out.
  // When any API call 401s, call /auth/refresh once and retry the original.
  // Concurrent 401s are queued so only ONE refresh fires. On refresh failure
  // the session is cleared (ProtectedRoute then redirects).
  useEffect(() => {
    let isRefreshing = false;
    let waiters = [];
    const flush = (err) => { waiters.forEach(w => err ? w.reject(err) : w.resolve()); waiters = []; };
    const id = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const url = original?.url || '';
        const skip = !original || original._retry || status !== 401
          || url.includes('/auth/refresh') || url.includes('/auth/login')
          || url.includes('/auth/register') || url.includes('/auth/logout')
          || url.includes('/public/') || url.includes('/customer/');
        if (skip) return Promise.reject(error);
        original._retry = true;
        if (isRefreshing) {
          await new Promise((resolve, reject) => waiters.push({ resolve, reject }));
          return axios(original);
        }
        isRefreshing = true;
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          flush(null);
          return axios(original);
        } catch (e) {
          flush(e);
          setUser(null);
          setCurrentOrg(null);
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }
    );
    // Now that the interceptor is active, check for an existing session.
    checkAuth();
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    if (data.memberships?.length > 0) {
      const first = data.memberships[0];
      setCurrentOrg(first);
      document.cookie = `current_org_id=${first.org_id};path=/;max-age=31536000`;
    }
    return data;
  };

  const register = async (email, password, name, invitationToken, organizationName) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      email, password, name,
      invitation_token: invitationToken || undefined,
      organization_name: organizationName || undefined,
    }, { withCredentials: true });
    if (data.pending_verification) {
      // Do not auto-login — return so caller shows "check your email" screen
      return data;
    }
    setUser(data);
    if (data.memberships?.length > 0) {
      const first = data.memberships[0];
      setCurrentOrg(first);
      document.cookie = `current_org_id=${first.org_id};path=/;max-age=31536000`;
    }
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setCurrentOrg(null);
  };

  const processGoogleSession = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google-session`, { session_id: sessionId }, { withCredentials: true });
    setUser(data);
    if (data.memberships?.length > 0) {
      setCurrentOrg(data.memberships[0]);
      document.cookie = `current_org_id=${data.memberships[0].org_id};path=/;max-age=31536000`;
    }
    return data;
  };

  const switchOrg = async (orgId) => {
    await axios.post(`${API}/organizations/switch`, { org_id: orgId }, { withCredentials: true });
    const org = user?.memberships?.find(m => m.org_id === orgId);
    setCurrentOrg(org);
    document.cookie = `current_org_id=${orgId};path=/;max-age=31536000`;
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      if (data.memberships?.length > 0 && !currentOrg) {
        setCurrentOrg(data.memberships[0]);
      } else if (currentOrg) {
        const updated = data.memberships?.find(m => m.org_id === currentOrg.org_id);
        if (updated) setCurrentOrg(updated);
      }
    } catch {}
  };

  const needsOnboarding = user && (!user.memberships || user.memberships.length === 0);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, processGoogleSession, checkAuth, currentOrg, setCurrentOrg, switchOrg, getOrgHeaders, needsOnboarding, refreshUser, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
